<template>
  <div>
    <h4 class="text-center">Select Facebook Page</h4>
    <div class="mt-5">
      <form @submit.prevent="promiseCreateChannel = createChannel()">
        <div class="row d-flex align-items-center">
          <div class="col-4 text-muted">
            Authenticated as:
          </div>
          <div class="col-6">
            <div class="row">
              <div class="col-3">
                <img :src="facebookUser.picture" :alt="facebookUser.name" class="rounded-circle" width="50px"/>
              </div>
              <div class="col-8">
                <div>{{ facebookUser.name }}</div>
                <a href="#" @click="logoutUser" class="text-muted">
                  <icon class="btn-icon" name="sign-out-alt"/>
                  not me
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group row mt-3">
          <label class="col-4 text-muted col-form-label">
            Facebook Page:
          </label>
          <div class="col-8">
            <spinner v-if="loadingFacebookPages"></spinner>
            <div v-else>
              <select class="form-control" :class="{'is-invalid': isFacebookPageInvalid}" id="select-facebook-page"
                      v-model="selectedFacebookPage">
                <option selected="selected" :value="null">
                  Select a page
                </option>
                <option v-for="page in facebookPages" :key="page.name" :value="page">
                  {{ page.name }}
                </option>
              </select>
              <div v-show="isFacebookPageInvalid" class="invalid-feedback">
                Please provide a Facebook Page.
              </div>
            </div>
          </div>
        </div>
        <div v-if="showNoFacebookPagesWarning" class="row">
          <div class="alert alert-warning col-12">
            No Facebook Page available. You have to be an administrator of Facebook Page to have it listed here.
            <a href="">Refresh page</a> after you've been added as administrator. See
            <a href="https://docs.amio.io/docs/facebook-messenger-getting-started-prepare-facebook-page" target="_blank">documentation</a>
            for more details.
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12 d-flex justify-content-center">
            <button class="btn btn-primary px-5"
                    v-busy-button="promiseCreateChannel">
              Connect
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import channelService from '@/services/amio-api/channel.service'
  import facebookService from '@/services/facebook/facebook.service'
  import Avatar from 'vue-avatar'
  import Spinner from 'vue-simple-spinner'
  import concat from 'ramda/src/concat'
  import 'vue-awesome/icons/sign-out-alt'

  export default {
    components: {
      'avatar': Avatar,
      'spinner': Spinner
    },

    store: {
      channel: 'administration.channelDetail.channel',
      loginStatus: 'administration.channelDetail.facebook.loginStatus'
    },

    data() {
      return {
        defaultMaxFacebookPages: parseInt(process.env.VUE_APP_LIST_MAX_SIZE),
        facebookPages: [],
        selectedFacebookPage: null,
        promiseCreateChannel: null,
        facebookUser: {
          name: '',
          picture: ''
        },
        isFacebookPageInvalid: null,
        showNoFacebookPagesWarning: false,
        loadingFacebookPages: false
      }
    },

    methods: {
      createChannel() {
        if (!this.selectedFacebookPage) {
          this.isFacebookPageInvalid = true
          return
        }

        this.isFacebookPageInvalid = null
        const data = {
          type: 'facebook_messenger',
          name: this.selectedFacebookPage.name,
          facebook_page: {
            id: this.selectedFacebookPage.id,
            access_token: this.selectedFacebookPage.access_token
          }
        }
        return channelService.create(data)
          .then(channel => {
            this.channel = channel
            this.clearCreateChannelPromise()
            this.$router.push({name: 'administration.channel-detail.info', params: {id: channel.id}})
          })
          .catch(this.clearCreateChannelPromise)
      },

      listFacebookAccounts() {
        log.debug('FacebookPage - listFacebookAccounts()')

        this.loadingFacebookPages = true
        return facebookService.listFacebookAccounts({max: this.defaultMaxFacebookPages})
          .then(facebookAccounts => {
            this.facebookPages = facebookAccounts.data
            return facebookAccounts
          })
          .then(this.loadNextFacebookPagesIfAny)
          .catch(error => {
            log.error('error while getting facebook accounts', error)
          })
          .then(this.loadingFacebookAccountsFinished)
      },

      loadingFacebookAccountsFinished() {
        this.showNoFacebookPagesWarning = this.facebookPages.length <= 0
        this.loadingFacebookPages = false
      },

      loadNextFacebookPagesIfAny(facebookAccounts) {
        const totalCount = facebookAccounts.summary.total_count

        if (totalCount <= this.defaultMaxFacebookPages) return

        let newMaxFacebookPages = totalCount
        if (newMaxFacebookPages > 100) {
          log.error('Can\'t load more then 100 facebook pages.')
          newMaxFacebookPages = 100
        }

        facebookService.listFacebookAccounts({max: newMaxFacebookPages, offset: this.defaultMaxFacebookPages})
          .then(facebookAccounts => {
            this.facebookPages = concat(this.facebookPages, facebookAccounts.data)
          })
          .catch(error => {
            log.error('error while getting facebook accounts', error)
          })
      },

      clearLoginStatus() {
        this.loginStatus = {}
      },

      clearCreateChannelPromise() {
        this.promiseCreateChannel = null
      },

      logoutUser() {
        facebookService.logout()
        this.clearLoginStatus()
      }
    },

    mounted() {
      this.listFacebookAccounts()
      const userId = this.loginStatus.authResponse.userID
      Promise.all([facebookService.getUserInfo(userId), facebookService.getUserPicture(userId)])
        .then(values => {
          this.facebookUser.name = values[0].name
          this.facebookUser.picture = values[1].data.url
        })
        .catch(error => log.error('error while getting facebook accounts', error))
    }
  }
</script>
